import { create, type StoreApi, useStore } from "zustand";
import { devtools } from "zustand/middleware";
import { createPersonSlice, type PersonSlice } from "@/store/person-slice";
import { createTestDemoSlice, type TestDemoSlice } from "@/store/test-demo-slice";
import { createVideoAnswersSlice, type VideoAnswersSlice } from "@/store/video-answers-slice";
import { type CenterStageSlice, createCenterStageSlice } from "./center-stage-slice";
import { type ControlledLoginSlice, createControlledLoginSlice } from "./controlled-login-slice";
import { type CvPageFormsSlice, createCvPageFormsSlice } from "./cv-page-forms-slice";
import { createEnvironmentSlice, type EnvironmentSlice } from "./environment-slice";
import { createJobExtractionSlice, type JobExtractionSlice } from "./job-extraction-slice";
import { createSidebarLayoutSlice, type SidebarLayoutSlice } from "./sidebar-layout-slice";
import { createStripeFormSlice, type StripeFormSlice } from "./stripe-form-slice";
import { createInterviewerSlice, type InterviewerSlice } from "./interviewer-slice";

type WithSelectors<S> = S extends { getState: () => infer T }
  ? S & { use: { [K in keyof T]: () => T[K] } }
  : never;

const createSelectors = <S extends StoreApi<object>>(_store: S) => {
  const store = _store as WithSelectors<typeof _store>;
  store.use = {};
  const storeState = Object.keys(store.getState());
  for (const k of storeState) {
    (store.use as unknown as { [key: string]: () => unknown })[k] = () =>
      useStore(_store, (s) => s[k as keyof typeof s]);
  }

  return store;
};

const useBoundStoreBase = create<
  PersonSlice &
    TestDemoSlice &
    CvPageFormsSlice &
    VideoAnswersSlice &
    EnvironmentSlice &
    CenterStageSlice &
    ControlledLoginSlice &
    StripeFormSlice &
    SidebarLayoutSlice &
    InterviewerSlice &
    JobExtractionSlice
>()(
  devtools((...a) => ({
    ...createPersonSlice(...a),
    ...createTestDemoSlice(...a),
    ...createCvPageFormsSlice(...a),
    ...createVideoAnswersSlice(...a),
    ...createEnvironmentSlice(...a),
    ...createCenterStageSlice(...a),
    ...createControlledLoginSlice(...a),
    ...createStripeFormSlice(...a),
    ...createSidebarLayoutSlice(...a),
    ...createInterviewerSlice(...a),
    ...createJobExtractionSlice(...a),
  }))
);

const useBoundStore = createSelectors(useBoundStoreBase);

export default useBoundStore;
